import React, { useMemo, useState } from 'react'
import { useTheme } from '@mui/material/styles'
import { useIsMobileLayout } from '../../../utils/hooks/useIsMobileLayout'
import { TableItem } from '../../layout/Datatable'
import { Column } from '../../table/ReactTable'
import DataTable from '../../table/DataTable'
import { DataTableFilterOption } from '../../table/DataTable.types'
import ExportCSV from '../../table/ExportCsv'
import { useAppTranslations } from '../../../utils/hooks/useAppTranslations'
import { getPerformanceStatsColumnTranslationKey } from '../../../utils/hooks/performanceStats/useStatsColumns'

export interface PerformanceStatsTableLoadingProps {
  error?: Error
  loading?: boolean
}

interface PerformanceStatsTableProps<T extends TableItem> extends PerformanceStatsTableLoadingProps {
  children?: React.ReactNode
  toDisplay: T[]
  columns: Column[]
  defaultSort?: Array<{ desc: boolean; id: string }>
}

export const PerformanceStatsTable: React.FC<PerformanceStatsTableProps<TableItem>> = ({
  columns,
  toDisplay,
  defaultSort = [],
  children,
  error,
  loading,
}) => {
  const t = useAppTranslations()
  const theme = useTheme()
  const isMobile = useIsMobileLayout()
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(
    columns
      .filter((column) => column.accessorKey !== 'name')
      .reduce<Record<string, boolean>>((acc, header) => Object.assign({}, acc, { [header.accessorKey]: true }), {})
  )

  const columnsWithVisibilityMeta = useMemo(
    () =>
      columns.map((column) => {
        const currentMeta = (column as any).meta
        const values = toDisplay
          .map((row) => row[column.accessorKey])
          .filter((value) => value !== null && value !== undefined)
        const isNumericColumn = values.length > 0 && values.every((value) => typeof value === 'number')
        const isNameColumn = column.accessorKey === 'name'
        const selectOptions = Array.from(new Set(values.map((value) => String(value)))).map<DataTableFilterOption>(
          (value) => ({
            label: value,
            value,
          })
        )

        return {
          ...column,
          meta: {
            ...currentMeta,
            columnLabel: isNameColumn
              ? String(column.header)
              : t(getPerformanceStatsColumnTranslationKey(column.accessorKey)),
            enableFilter: currentMeta?.enableFilter ?? true,
            enableNullFilter: toDisplay.some(
              (row) => row[column.accessorKey] === null || row[column.accessorKey] === undefined
            ),
            filterOptions: currentMeta?.filterOptions ?? (!isNumericColumn ? selectOptions : undefined),
            filterVariant: currentMeta?.filterVariant ?? (isNumericColumn ? 'number' : 'select'),
            hideable: column.accessorKey !== 'name',
          },
        }
      }),
    [columns, t, toDisplay]
  )

  const statsTableContainerSx = useMemo(
    () =>
      isMobile
        ? {
            '& .MuiTableHead-root > .MuiTableRow-root:first-of-type .MuiTableCell-root': {
              whiteSpace: 'normal',
            },
            '& .MuiTableCell-root': {
              minWidth: 0,
              width: 'auto',
            },
          }
        : undefined,
    [isMobile]
  )

  return (
    <DataTable
      columnVisibility={columnVisibility}
      columns={columnsWithVisibilityMeta}
      containerSx={statsTableContainerSx}
      darkHeader
      data={toDisplay}
      defaultSorting={defaultSort}
      enableColumnFilters
      enableColumnVisibility
      enableToolbar={!isMobile}
      error={error}
      footerTop
      loading={loading ?? false}
      onColumnVisibilityChange={setColumnVisibility}
      skeletonProps={{ enableToolbar: !isMobile, lineNumbers: 5 }}
      stickyHeader
      toolbarEnd={
        <>
          {children}
          <ExportCSV data={toDisplay} columns={columns} sx={{ ml: (theme) => theme.spacing(1) }} />
        </>
      }
      useGroupStyle
    />
  )
}
